import { Button, Card, Form, notification, Space, Table } from "antd";
import { useTranslation } from "react-i18next";
import { formatCurrencyRevenue } from "features/revenue/utils";
import { useCallback, useState } from "react";
import { CloudUploadOutlined } from "@ant-design/icons";
import * as XLSX from 'xlsx';
import moment from "moment";
import revenueApi from "api/revenue";
import AdminBudgetImport from "./AdminBudgetImport";
import AdminBudgetExport from "./AdminBudgetExport";

export default function AdminBudgetTable({ budgets = [], month, onReload }) {
    const { t } = useTranslation()
    const [api, contextHolder] = notification.useNotification();
    const [formImport] = Form.useForm();
    const [openImport, setOpenImport] = useState(false);
    const [loading, setLoading] = useState(false);

    const notify = useCallback((type, message, description) => {
        api[type]({ message, description });
    }, [api]);

    const closeModalImport = () => {
        setOpenImport(false);
        formImport.resetFields();
    };

    const onImport = useCallback(async (values) => {
        try {
            setLoading(true);
            const reader = new FileReader();
            const rABS = !!reader.readAsBinaryString;

            reader.onload = async (e) => {
                const bstr = e.target.result;
                const wb = XLSX.read(bstr, { type: rABS ? "binary" : "array" });
                const ws = wb.Sheets[wb.SheetNames[0]];
                // header: 1 → array of arrays (no merged-cell key problem)
                const raw = XLSX.utils.sheet_to_json(ws, { header: 1 });

                if (!Array.isArray(raw) || raw.length < 2) {
                    notify("error", t('error'), t('wrongFileFormat'));
                    setLoading(false);
                    return;
                }

                // Tự tìm row chứa header columns (có thể row 0 là title, row 1 là headers)
                const HEADER_KEYS = ['shopCode', 'shopName', 'budget', 'productivity'];
                const headerRowIdx = raw.findIndex(row =>
                    Array.isArray(row) && HEADER_KEYS.some(k => row.includes(k))
                );

                if (headerRowIdx === -1) {
                    notify("error", t('error'), t('wrongFileFormat'));
                    setLoading(false);
                    return;
                }

                const headers = raw[headerRowIdx];
                const dataRows = raw.slice(headerRowIdx + 1);

                // Build shopCode → shopId map từ existing budgets
                const budgetMap = {};
                budgets.forEach(b => { budgetMap[b.shopCode] = b.shopId; });

                const parseNum = (val) => {
                    if (val == null || val === '') return null;
                    const n = parseFloat(String(val).replace('%', ''));
                    return isNaN(n) ? null : n;
                };

                const shops = dataRows
                    .map(row => {
                        const shopCode = row[headers.indexOf('shopCode')] ?? '';
                        const budget = parseNum(row[headers.indexOf('budget')]);
                        const productivity = parseNum(row[headers.indexOf('productivity')]);
                        return {
                            shopId: budgetMap[String(shopCode).trim()],
                            budget,
                            productivity,
                        };
                    })
                    .filter(item => item.shopId && (item.budget != null || item.productivity != null));

                if (shops.length === 0) {
                    notify("error", t('error'), t('wrongFileFormat'));
                    setLoading(false);
                    return;
                }

                try {
                    await revenueApi.saveMultiShopBudget({ month, shops });
                    notify("success", t('notification'), t('importSuccess'));
                    closeModalImport();
                    onReload?.();
                } catch (err) {
                    notify("error", t('error'), err?.response?.data?.message || t('hasError'));
                    setLoading(false);
                }
            };

            if (rABS) reader.readAsBinaryString(values.upload[0].originFileObj);
            else reader.readAsArrayBuffer(values.upload[0].originFileObj);
        } catch (error) {
            notify("error", t('error'), t('hasError'));
            setLoading(false);
        }
    }, [budgets, month, notify, t, onReload]);

    return (
        <>
            {contextHolder}
            <AdminBudgetImport
                loading={loading}
                show={openImport}
                onFinish={onImport}
                form={formImport}
                onCancel={closeModalImport}
            />
            <Card
                title={
                    <Space>
                        <span>{t('budget')}</span>
                    </Space>
                }
                extra={
                    <Space>
                        <Button
                            size="large"
                            icon={<CloudUploadOutlined />}
                            onClick={() => setOpenImport(true)}
                        >
                            {t('import')}
                        </Button>
                        <AdminBudgetExport month={month} budgets={budgets} />
                    </Space>
                }
                style={{ marginTop: 16 }}
            >
                <Table
                    size="small"
                    columns={[
                        {
                            title: t('shopCode'),
                            dataIndex: 'shopCode',
                            key: 'shopCode',
                            width: 120,
                            fixed: 'left',
                            align: 'center',
                        },
                        {
                            title: t('shopName'),
                            dataIndex: 'shopName',
                            key: 'shopName',
                            width: 180,
                        },
                        {
                            title: t('budget'),
                            dataIndex: 'budget',
                            key: 'budget',
                            width: 150,
                            align: 'right',
                            render: (val) => val ? formatCurrencyRevenue(val) : '-',
                        },
                        {
                            title: t('productivity'),
                            dataIndex: 'productivity',
                            key: 'productivity',
                            width: 130,
                            align: 'right',
                            render: (val) => val ? `${val}%` : '-',
                        },
                    ]}
                    dataSource={budgets}
                    rowKey="shopId"
                    pagination={false}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </>
    );
}
